import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export type ReactionBarTone = 'glass-light' | 'glass-dark'

export interface Reaction {
  id: string
  emoji: React.ReactNode
  count: number
  /** Warmer, larger treatment for a primary reaction (e.g. "love") vs. secondary ones */
  emphasis?: boolean
}

export interface ReactionBarProps extends Omit<BoxProps, 'onChange'> {
  reactions: Reaction[]
  /** Ids of reactions the current viewer has toggled on */
  activeIds?: string[]
  onToggle?: (id: string) => void
  /** `glass-light` sits on a photo/dark-image background; `glass-dark` sits on already-dark chrome (lightbox dock) */
  tone?: ReactionBarTone
}

const Row = styled(Box)({
  display: 'inline-flex',
  gap: 6,
  alignItems: 'center',
})

const toneStyles: Record<ReactionBarTone, React.CSSProperties> = {
  'glass-light': {
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.16)',
    color: '#fff',
  },
  'glass-dark': {
    background: 'rgba(244,243,246,0.07)',
    border: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
    color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))',
  },
}

const toneHoverBg: Record<ReactionBarTone, string> = {
  'glass-light': 'rgba(255,255,255,0.2)',
  'glass-dark': 'rgba(244,243,246,0.12)',
}

const Pill = styled('button', {
  shouldForwardProp: (p) => p !== 'tone' && p !== '$emphasis' && p !== '$active',
})<{ tone: ReactionBarTone; $emphasis?: boolean; $active?: boolean }>(({ tone, $emphasis, $active }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: $emphasis ? 36 : 32,
  padding: $emphasis ? '0 16px' : '0 12px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: $emphasis ? 13 : 12.5,
  fontWeight: $emphasis ? 650 : 500,
  cursor: 'pointer',
  transition: 'background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)',
  ...(($emphasis
    ? $active
      ? { background: 'var(--ds-accent-deep-a, #e97a4a)', color: '#4a1e0a' }
      : { background: 'rgba(254,166,125,0.14)', border: '1px solid rgba(254,166,125,0.35)', color: 'var(--ds-accent-deep-a, #e97a4a)' }
    : toneStyles[tone]) as React.CSSProperties),
  '&:hover': $emphasis ? undefined : { background: toneHoverBg[tone] },
}))

const Count = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  opacity: 0.85,
})

/** Row of emoji+count reaction pills, toggleable — over a photo hero or in the lightbox dock. */
export function ReactionBar({ reactions, activeIds = [], onToggle, tone = 'glass-light', ...rest }: ReactionBarProps) {
  return (
    <Row role='group' {...rest}>
      {reactions.map((r) => {
        const active = activeIds.includes(r.id)
        return (
          <Pill
            key={r.id}
            type='button'
            tone={tone}
            $emphasis={r.emphasis}
            $active={active}
            aria-pressed={active}
            onClick={() => onToggle?.(r.id)}
          >
            {r.emoji}
            <Count>{r.count}</Count>
          </Pill>
        )
      })}
    </Row>
  )
}
